// Ballpark Overview: the season's search activity in the Ballpark app. Same sections, order and grids as
// before, styled like the rest of the dashboard (panels, headline cards, tables, chart theme).
// Each section only shows when the backend sends its data.
import React from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { ArrowRight, Utensils, ShoppingBag, DoorOpen, Timer, Clock } from 'lucide-react';
import { Panel, PanelHeader } from '../../Concession/components/ConcessionUI';
import { KpiCard } from '../../Concession/live/LiveComponents';
import { CHART_THEME } from '../../Concession/charts/chartTheme';
import '../../Concession/concession.css';
import './ballpark.css';

// Known type config — only these render in the search type breakdown
const SEARCH_TYPE_CONFIG = {
  concession: { label: 'Concessions', icon: Utensils, color: '#10b981' },
  retail: { label: 'Retail', icon: ShoppingBag, color: '#3b82f6' },
  gate: { label: 'Gates', icon: DoorOpen, color: '#f59e0b' },
};
const UNMATCHED_COLOR = '#dc2626';

// Chart colors (no brand teal on data)
const SESSIONS_COLOR = '#3e58c0';
const PER_SESSION_COLOR = '#9792ec';
const DURATION_COLOR = '#ec4899';

// Shared recharts styling, matching the dashboard's chart theme
const axis = { stroke: CHART_THEME.text, fontSize: 12, tickLine: false, axisLine: false, fontFamily: CHART_THEME.font };
const grid = { strokeDasharray: '0', stroke: CHART_THEME.grid, vertical: false };
const tooltip = {
  contentStyle: {
    background: 'rgba(26, 26, 26, 0.92)', border: 'none', borderRadius: 10, padding: '8px 12px', fontFamily: CHART_THEME.font,
  },
  labelStyle: { color: '#ffffff', fontWeight: 600, marginBottom: 2 },
  itemStyle: { color: '#ffffff' },
  cursor: { fill: 'rgba(16, 24, 40, 0.04)' },
};

const fmt = (n) => (typeof n === 'number' && !Number.isNaN(n) ? Math.round(n).toLocaleString() : '0');

// Type tag for tables and journeys ("No match" when the term matched no location)
const TypeTag = ({ type, unmatched = false }) => {
  const config = SEARCH_TYPE_CONFIG[type];
  if (!config && !unmatched) return null;
  return (
    <span className="bp-tag">
      <span className="bp-tag-dot" style={{ background: config ? config.color : UNMATCHED_COLOR }} />
      {config ? config.label : 'No match'}
    </span>
  );
};

// Search journey card
const SearchJourneyCard = ({ journey }) => (
  <div className="bp-card">
    <div className="bp-journey">
      {journey.journey.map((step, idx) => (
        <React.Fragment key={idx}>
          <span className="bp-chip">{step}</span>
          {idx < journey.journey.length - 1 && <ArrowRight size={14} />}
        </React.Fragment>
      ))}
      <TypeTag type={journey.type} />
    </div>
    <div className="bp-card-meta">
      <span><strong>{fmt(journey.count)}</strong> sessions</span>
      {journey.conversion != null && <span><strong>{journey.conversion}%</strong> conversion</span>}
    </div>
  </div>
);

// One search type: searches, share, top terms, session length
const SearchTypeCard = ({ typeKey, data }) => {
  const config = SEARCH_TYPE_CONFIG[typeKey];
  if (!config) return null;
  const { label, icon: Icon, color } = config;
  return (
    <div className="bp-type" style={{ '--bp-type': color }}>
      <div className="bp-type-head">
        <span className="bp-type-icon"><Icon size={18} /></span>
        <span className="bp-type-name">{label}</span>
        <span className="bp-type-share">{data.percentage || 0}%</span>
      </div>
      <div className="bp-type-value">
        {fmt(data.count || 0)}
        <span>searches</span>
      </div>
      <div className="bp-type-bar"><i style={{ width: `${Math.min(100, data.percentage || 0)}%` }} /></div>
      {data.topTerms && data.topTerms.length > 0 && (
        <div className="bp-type-terms">
          <span className="bp-mini-label">Top search terms</span>
          <div className="bp-chip-row">
            {data.topTerms.map((term, idx) => <span key={idx} className="bp-chip">{term}</span>)}
          </div>
        </div>
      )}
      {data.avgSessionDuration && (
        <div className="bp-type-foot">
          <Timer size={15} />
          Avg session <strong>{data.avgSessionDuration}</strong>
        </div>
      )}
    </div>
  );
};

// Ranked terms table
const TermsTable = ({ items, showType, maxHeight }) => (
  <div className="bp-table-wrap" style={{ maxHeight }}>
    <table className="bp-table">
      <thead>
        <tr>
          <th className="bp-rank">#</th>
          <th>Term</th>
          <th className="bp-num">{showType ? 'Count' : 'Searches'}</th>
          {showType && <th className="bp-num">Type</th>}
        </tr>
      </thead>
      <tbody>
        {items.map((item, idx) => (
          <tr key={item.term}>
            <td className="bp-rank">{idx + 1}</td>
            <td className="bp-term">{item.term}</td>
            <td className="bp-num">{fmt(item.count)}</td>
            {showType && <td className="bp-num"><TypeTag type={item.type} unmatched={!SEARCH_TYPE_CONFIG[item.type]} /></td>}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

// New or returning users
const UserTypeCard = ({ title, data, color }) => (
  <div className="bp-card">
    <div className="bp-card-head">
      <span className="bp-card-title"><span className="cx-ov-swatch" style={{ background: color }} />{title}</span>
      <span className="bp-user-count">{fmt(data.count)}</span>
    </div>
    <div className="bp-card-meta">
      <span>Avg <strong>{data.avgSearches}</strong> searches</span>
      <span>Top <strong>{data.topSearch}</strong></span>
    </div>
    <div className="bp-card-foot">
      <Timer size={15} />
      Avg session <strong>{data.avgSessionDuration}</strong>
    </div>
  </div>
);

const BallparkOverview = ({
  overallMetrics,
  searchTypeBreakdown,
  noResultsSearches,
  topSearchTerms,
  sessionsOverTime,
  searchDistribution,
  sessionDurationDistribution,
  searchJourneys,
  userTypeBreakdown,
}) => {

  // Filter to only known types that have data
  const activeSearchTypes = Object.keys(searchTypeBreakdown || {})
    .filter(key => SEARCH_TYPE_CONFIG[key] && searchTypeBreakdown[key]?.count > 0);

  return (
    <div className="bp-view">

      {/* Summary Stats */}
      <section className="bp-section">
        <div className="cx-kpi-grid cx-kpi-grid-auto bp-kpis">
          <KpiCard label="Total sessions" info="Times fans opened Ballpark this season." value={fmt(overallMetrics?.totalSessions || 0)}>
            <div className="cx-kpi-foot">This season</div>
          </KpiCard>
          <KpiCard label="Total searches" info="Searches fans made in Ballpark this season." value={fmt(overallMetrics?.totalSearches || 0)}>
            <div className="cx-kpi-foot">This season</div>
          </KpiCard>
          <KpiCard label="Avg searches / session" info="Average number of searches in a session." value={(overallMetrics?.avgSearchesPerSession || 0).toFixed(1)}>
            <div className="cx-kpi-foot">Per session</div>
          </KpiCard>
          <KpiCard label="Unique users" info="Different fans who used Ballpark this season." value={fmt(overallMetrics?.uniqueUsers || 0)}>
            <div className="cx-kpi-foot">This season</div>
          </KpiCard>
          <KpiCard label="Avg session length" info="Average time a fan spent in a Ballpark session." value={overallMetrics?.avgSessionDurationFormatted || '0:00'}>
            <div className="cx-kpi-sub">Median {overallMetrics?.medianSessionDurationFormatted || '0:00'}</div>
          </KpiCard>
        </div>
      </section>

      {/* SEARCH TYPE BREAKDOWN — dynamic based on backend data */}
      {activeSearchTypes.length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Search type breakdown" subtitle="Search activity by location type." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))' }}>
            {activeSearchTypes.map(typeKey => (
              <SearchTypeCard key={typeKey} typeKey={typeKey} data={searchTypeBreakdown[typeKey]} />
            ))}

            {/* Session Duration Distribution */}
            {(sessionDurationDistribution || []).length > 0 && (
              <div className="bp-type" style={{ '--bp-type': DURATION_COLOR }}>
                <div className="bp-type-head">
                  <span className="bp-type-icon"><Clock size={18} /></span>
                  <span className="bp-type-name">Session duration</span>
                </div>
                <div style={{ height: '180px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={sessionDurationDistribution} layout="vertical" margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} vertical horizontal={false} />
                      <XAxis type="number" {...axis} />
                      <YAxis dataKey="duration" type="category" {...axis} width={70} />
                      <Tooltip {...tooltip} formatter={(value) => [`${value} sessions`, 'Count']} />
                      <Bar dataKey="count" fill={DURATION_COLOR} radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* DEMAND */}
      {(noResultsSearches || []).length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Demand" subtitle="What fans searched for by location type, and the searches that matched no location." />
          {/* Both sides stretch to the taller one */}
          <div className="bp-grid bp-grid-equal" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {/* No Results Table */}
            <div className="bp-fill">
              <div className="bp-sub-title">Top unmatched searches</div>
              <p className="bp-sub-text">Terms fans searched that don't match any tagged location.</p>
              <TermsTable items={noResultsSearches || []} maxHeight={360} />
            </div>

            {/* Demand by Category */}
            {(activeSearchTypes.length > 0 || (noResultsSearches || []).length > 0) && (() => {
              const unmatchedCount = (noResultsSearches || []).reduce((sum, item) => sum + item.count, 0);
              const classifiedTotal = activeSearchTypes.reduce((sum, key) => sum + (searchTypeBreakdown[key]?.count || 0), 0);
              const grandTotal = classifiedTotal + unmatchedCount;

              const pieData = [
                ...activeSearchTypes.map(typeKey => ({
                  name: SEARCH_TYPE_CONFIG[typeKey].label,
                  value: searchTypeBreakdown[typeKey].count,
                  percentage: grandTotal > 0 ? Math.round(searchTypeBreakdown[typeKey].count / grandTotal * 1000) / 10 : 0,
                  color: SEARCH_TYPE_CONFIG[typeKey].color,
                })),
                ...(unmatchedCount > 0 ? [{
                  name: 'Unmatched',
                  value: unmatchedCount,
                  percentage: grandTotal > 0 ? Math.round(unmatchedCount / grandTotal * 1000) / 10 : 0,
                  color: UNMATCHED_COLOR,
                }] : []),
              ];

              return (
                <div className="bp-inset">
                  <div className="bp-sub-title">Demand by category</div>
                  <p className="bp-sub-text">Share of all searches, including the ones that matched nothing.</p>
                  <div className="bp-donut">
                    <div className="bp-donut-chart">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius="100%"
                            innerRadius="66%"
                            paddingAngle={2}
                            stroke="#ffffff"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={index} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip {...tooltip} formatter={(value, name) => [value.toLocaleString() + ' searches', name]} />
                        </PieChart>
                      </ResponsiveContainer>
                      <div className="bp-donut-center">
                        <strong>{fmt(grandTotal)}</strong>
                        <span>searches</span>
                      </div>
                    </div>
                    <ul className="bp-donut-legend">
                      {pieData.map((d) => (
                        <li key={d.name}>
                          <span className="cx-ov-swatch" style={{ background: d.color }} />
                          <span className="bp-donut-name">{d.name}</span>
                          <span className="bp-donut-val">{fmt(d.value)}</span>
                          <span className="bp-donut-share">{d.percentage}%</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              );
            })()}
          </div>
        </Panel>
      )}

      {/* TOP SEARCH TERMS */}
      {(topSearchTerms || []).length > 0 && (
        <Panel className="bp-section">
          <PanelHeader title="Top search terms" subtitle="What fans searched for most this season." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>
            {/* Bar Chart */}
            <div>
              <div className="bp-sub-title">Search term rankings</div>
              <div style={{ height: '300px' }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={(topSearchTerms || []).slice(0, 10)} layout="vertical" margin={{ left: 0, right: 8 }}>
                    <CartesianGrid {...grid} vertical horizontal={false} />
                    <XAxis type="number" {...axis} />
                    <YAxis dataKey="term" type="category" {...axis} width={120} />
                    <Tooltip {...tooltip} formatter={(value) => [`${value.toLocaleString()} searches`, 'Count']} />
                    <Bar dataKey="count" radius={[0, 4, 4, 0]}>
                      {(topSearchTerms || []).slice(0, 10).map((item, idx) => (
                        <Cell key={idx} fill={SEARCH_TYPE_CONFIG[item.type]?.color || UNMATCHED_COLOR} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Detail Table */}
            <div>
              <div className="bp-sub-title">Search terms detail</div>
              <TermsTable items={topSearchTerms || []} showType maxHeight={300} />
            </div>
          </div>
        </Panel>
      )}

      {/* ACTIVITY */}
      {((sessionsOverTime || []).length > 0 || (searchDistribution || []).length > 0) && (
        <Panel className="bp-section">
          <PanelHeader title="Activity" subtitle="When fans used Ballpark and how much they searched." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(400px, 1fr))' }}>
            {/* Sessions Per Date */}
            {(sessionsOverTime || []).length > 0 && (
              <div>
                <div className="bp-sub-title">Sessions per date</div>
                <p className="bp-sub-text">Daily session activity across the season.</p>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={sessionsOverTime || []} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} />
                      <XAxis dataKey="date" {...axis} fontSize={11} angle={-45} textAnchor="end" height={60} />
                      <YAxis {...axis} />
                      <Tooltip {...tooltip} cursor={{ stroke: CHART_THEME.grid }} formatter={(value) => [`${value.toLocaleString()} sessions`, 'Sessions']} />
                      <Line type="monotone" dataKey="sessions" stroke={SESSIONS_COLOR} strokeWidth={2.25} dot={{ fill: SESSIONS_COLOR, r: 3, strokeWidth: 0 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}

            {/* Searches Per Session */}
            {(searchDistribution || []).length > 0 && (
              <div>
                <div className="bp-sub-title">Searches per session</div>
                <p className="bp-sub-text">How many searches fans made in a session.</p>
                <div style={{ height: '250px' }}>
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={searchDistribution || []} margin={{ left: 0, right: 8 }}>
                      <CartesianGrid {...grid} />
                      <XAxis dataKey="searches" {...axis} />
                      <YAxis {...axis} />
                      <Tooltip {...tooltip} formatter={(value) => [`${value.toLocaleString()} sessions`, 'Sessions']} />
                      <Bar dataKey="count" fill={PER_SESSION_COLOR} radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        </Panel>
      )}

      {/* FAN BEHAVIOR — Search Journeys + User Type Breakdown */}
      {((searchJourneys || []).length > 0 || (userTypeBreakdown?.newUsers?.count > 0 || userTypeBreakdown?.returningUsers?.count > 0)) && (
        <Panel className="bp-section">
          <PanelHeader title="Fan behavior" subtitle="How fans search and what patterns emerge." />
          <div className="bp-grid" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))' }}>

            {/* Search Journeys */}
            {(searchJourneys || []).length > 0 && (
              <div>
                <div className="bp-sub-title">Common search journeys</div>
                <div className="bp-stack">
                  {searchJourneys.map((journey, idx) => (
                    <SearchJourneyCard key={idx} journey={journey} />
                  ))}
                </div>
              </div>
            )}

            {/* New vs Returning Users */}
            {userTypeBreakdown && (userTypeBreakdown.newUsers?.count > 0 || userTypeBreakdown.returningUsers?.count > 0) && (
              <div>
                <div className="bp-sub-title">New vs returning users</div>
                <div className="bp-stack">
                  {userTypeBreakdown.newUsers?.count > 0 && (
                    <UserTypeCard title="New users" data={userTypeBreakdown.newUsers} color="#6366f1" />
                  )}
                  {userTypeBreakdown.returningUsers?.count > 0 && (
                    <UserTypeCard title="Returning users" data={userTypeBreakdown.returningUsers} color="#10b981" />
                  )}
                </div>
              </div>
            )}

          </div>
        </Panel>
      )}

    </div>
  );
};

export default BallparkOverview;
